<template>
  <DataFetcher
    :dataKey="dataKey"
    :refreshOnEvents="refreshOnEvents"
    :refreshDelay="refreshDelay"
    :log="log"
    :debounceDelay="debounceDelay"
    :filters="{
      page: internalPage,
      limit: internalLimit,
      ...(filters || {}),
    }"
    @fetched="onFetched"
    @beforeGetting="onBeforeGetting"
    @startGetting="onStartGetting"
    @loadingError="onLoadingError"
  >
    <template #any="{ refresh, result }">
      <slot
        name="error"
        :refresh="refresh"
        :result="result"
        v-if="loadingError"
      >
        <NoDataView :refresh="refresh" :result="result"></NoDataView>
      </slot>
      <template v-else>
        <slot
          :refresh="refresh"
          :result="result"
          name="empty"
          v-if="!loading && !items.length"
        >
          <NoDataView :refresh="refresh" :result="result"></NoDataView>
        </slot>
        <slot
          v-else
          name="loadingAndItems"
          :items="items"
          :refresh="refresh"
          :limit="currentLimit"
          :total="total"
          :loading="loading"
        >
          <slot v-if="loading" name="loadingContainer">
            <div class="w-full h-24 shimmerElement"></div>
          </slot>
          <div v-else class="w-full overflow-auto">
            <slot
              name="items"
              :items="items"
              :refresh="refresh"
              :limit="currentLimit"
              :total="total"
            >
              <slot
                v-for="(item, i) in items"
                :name="'item'"
                :item="item"
                :refresh="refresh"
                :index="i"
                :key="i"
                :isLast="i == items.length - 1"
                :isFirst="i == 0"
              >
                <div class="border m-2 p-2" v-html="jsonToHtml(item)"></div>
              </slot>
            </slot>
          </div>
        </slot>

        <div class="w-full" :class="{ 'pointer-events-none': loading }">
          <slot
            name="pagination"
            :page="currentPage"
            :totalPages="totalPages"
            :limit="currentLimit"
            :total="total"
            :changePage="changePage"
            :changeLimit="changeLimit"
            :nextPage="
              () => {
                changePage(currentPage + 1)
              }
            "
            :prevPage="
              () => {
                changePage(currentPage - 1)
              }
            "
          >
            <div class="px-6 py-3 bg-gray-50 flex justify-between items-center">
              <span class="text-sm text-gray-700"
                >Page {{ currentPage + 1 }} of {{ totalPages }}</span
              >
              <div class="space-x-2">
                <button
                  @click="changePage(currentPage - 1)"
                  class="px-3 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                >
                  &lt;
                </button>
                <button
                  @click="changePage(currentPage + 1)"
                  class="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  &gt;
                </button>
              </div>
              <!-- <label class="ml-4">
              Per page:
              <select :value="currentLimit" @change="onLimitChange($event)">
                <option v-for="opt in [5, 10, 20, 50]" :key="opt" :value="opt">
                  {{ opt }}
                </option>
              </select>
            </label> -->
            </div>
          </slot>
        </div>
      </template>
    </template>
  </DataFetcher>
</template>

<script>
import { defineComponent } from 'vue'
import DataFetcher from './DataFetcher.vue'
import NoDataView from './NoDataView.vue'
import Commons from '@/shared/utils/commons'

export default defineComponent({
  components: { DataFetcher, NoDataView },
  name: 'itemsList',
  emits: ['update:page', 'update:limit', 'fetched', 'dataChanged'],
  props: {
    dataKey: { type: String, default: '' },
    page: { type: [Number, String], default: 0 },
    limit: { type: [Number, String], default: 10 },
    filters: { type: Object, default: {} },
    refreshDelay: { type: Number },
    refreshOnEvents: { type: [String, Array] },
    debounceDelay: { type: Number },
    log: { type: Boolean, default: false },
  },
  data() {
    return {
      items: [],
      oldItems: [],
      loadingError: false,
      loading: true,
      internalPage: Number.isInteger(Number(this.page)) ? Number(this.page) : 0,
      internalLimit: Number.isInteger(Number(this.limit))
        ? Number(this.limit)
        : 10,
      total: 0,
    }
  },
  computed: {
    totalPages() {
      if (!this.total || this.internalLimit <= 0) return 1
      return Math.max(1, Math.ceil(this.total / this.internalLimit))
    },
    currentPage() {
      return this.internalPage
    },
    currentLimit() {
      return this.internalLimit
    },
  },
  mounted() {},
  methods: {
    onFetched({ data }) {
      if (data) {
        this.loading = false
        this.loadingError = false
        this.items = Array.isArray(data) ? data : data.result || []
        this.total = Array.isArray(data)
          ? data.length
          : data.total || this.items.length
        this.$emit('fetched', {
          items: this.items,
          total: this.total,
          page: this.internalPage,
          limit: this.internalLimit,
        })
        if (!Commons.deepEqual(this.items, this.oldItems)) {
          this.$emit('dataChanged', {
            old: this.oldItems,
            new: this.items,
          })
        }
        this.oldItems = [...this.items]
      }
    },
    onBeforeGetting() {
      this.loading = true
      this.loadingError = false
    },
    onStartGetting() {
      this.loading = true
      this.loadingError = false
    },
    onLoadingError() {
      this.loadingError = true
      this.loading = false
    },
    changePage(next) {
      const n = Number(next)
      if (!Number.isInteger(n) || n < 0 || n >= this.totalPages) return
      if (n === this.internalPage) return
      this.internalPage = n
    },
    changeLimit(nextLimit) {
      const n = Number(nextLimit)
      if (!Number.isInteger(n) || n <= 0) return
      this.internalLimit = n
      this.internalPage = 0
    },
    onLimitChange(ev) {
      const val = Number(ev.target.value)
      this.changeLimit(val)
    },
    jsonToHtml(obj) {
      return Commons.jsonToHtml(obj)
    },
  },
})
</script>
